'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

type NodeType = 'activity' | 'kpi' | 'bvo' | 'outcome';
type NodeHealth = 'good' | 'warn' | 'bad' | 'neutral';

interface FlowNode {
  id: string;
  label: string;
  sublabel?: string;
  type: NodeType;
  health: NodeHealth;
  value?: string;
}

const inspectorData: Record<string, {
  kpis: Array<{ label: string; value: string; trend: string; direction: 'up' | 'down' | 'flat' }>;
  risks: string[];
  impact: string;
  recommendation: string;
}> = {
  'n-po': {
    kpis: [
      { label: 'PO Lead Time', value: '6.4 days', trend: '+0.8d vs last month', direction: 'down' },
      { label: 'PO Accuracy Rate', value: '94.2%', trend: '-1.1% vs last month', direction: 'down' },
      { label: 'Monthly Volume', value: '1,205 orders', trend: '+3.2% vs last month', direction: 'up' },
    ],
    risks: ['Lead time increasing trend over 4 weeks', 'Manual entry errors in 5.8% of POs'],
    impact: 'Delayed POs extend supplier invoice cycle by avg 1.4 days, increasing DPO risk.',
    recommendation: 'Enable automated PO generation from approved vendor catalog to reduce lead time.',
  },
  'n-bvo-pay': {
    kpis: [
      { label: 'Health Score', value: '54/100', trend: '-8 pts vs last week', direction: 'down' },
      { label: 'On-Time Payment Rate', value: '71%', trend: '-5% vs last month', direction: 'down' },
      { label: 'Late Payment Penalties', value: '$42K', trend: '+$18K vs last month', direction: 'down' },
    ],
    risks: ['On-time payment rate below 75% threshold', 'Penalty accrual trending upward', 'Manual approval bottleneck in payment run'],
    impact: 'Late payments damaging supplier relationships and accruing $42K in penalties this period.',
    recommendation: 'Automate payment approvals under $50K and implement early payment discount program.',
  },
  default: {
    kpis: [
      { label: 'Process Volume', value: '1,186/mo', trend: '+2.1% vs last month', direction: 'up' },
      { label: 'Cycle Time', value: '3.2 days', trend: '-0.3d vs last month', direction: 'up' },
      { label: 'Conformance Rate', value: '82%', trend: '+1% vs last month', direction: 'up' },
    ],
    risks: ['Minor conformance deviation in variant B'],
    impact: 'This node contributes positively to downstream BVO health.',
    recommendation: 'Monitor conformance weekly and review variant B for automation opportunities.',
  },
};

const typeColors: Record<NodeType, string> = {
  activity: 'text-blue-400',
  kpi: 'text-primary',
  bvo: 'text-accent',
  outcome: 'text-positive',
};

const typeBg: Record<NodeType, string> = {
  activity: 'bg-blue-500/10 border-blue-500/20',
  kpi: 'bg-primary/10 border-primary/20',
  bvo: 'bg-accent/10 border-accent/20',
  outcome: 'bg-positive/10 border-positive/20',
};

const healthColors: Record<NodeHealth, string> = {
  good: 'text-positive',
  warn: 'text-warning',
  bad: 'text-negative',
  neutral: 'text-muted-foreground',
};

const healthLabels: Record<NodeHealth, string> = {
  good: 'Healthy',
  warn: 'At Risk',
  bad: 'Critical',
  neutral: 'Unknown',
};

export default function FlowNodeInspector({ node, onClose }: { node: FlowNode; onClose: () => void }) {
  const [tab, setTab] = useState('KPIs');
  const data = inspectorData[node.id] || inspectorData.default;

  return (
    <div className="w-72 shrink-0 bg-card rounded-xl card-glow flex flex-col overflow-hidden animate-fade-in">
      <div className="px-4 py-3 border-b border-border">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className={`inline-flex items-center gap-1.5 text-2xs font-semibold px-2 py-0.5 rounded border mb-2 ${typeBg[node.type]}`}>
              <span className={`capitalize ${typeColors[node.type]}`}>{node.type}</span>
            </div>
            <p className="text-sm font-semibold text-foreground leading-tight">{node.label}</p>
            {node.sublabel && <p className="text-xs text-muted-foreground mt-0.5">{node.sublabel}</p>}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors shrink-0">
            <Icon name="XMarkIcon" size={14} />
          </button>
        </div>
        <div className={`flex items-center gap-1.5 mt-2 text-xs font-medium ${healthColors[node.health]}`}>
          <span className={`w-2 h-2 rounded-full ${
            node.health === 'good' ? 'bg-positive' :
            node.health === 'warn' ? 'bg-warning' : 'bg-negative'
          }`} />
          {healthLabels[node.health]}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        {['KPIs', 'Risks', 'Impact'].map((t) => (
          <button
            key={`inspector-tab-${t}`}
            onClick={() => setTab(t)}
            className={`flex-1 py-2.5 text-xs font-medium transition-colors border-b-2 ${
              tab === t ? 'text-primary border-primary' : 'text-muted-foreground border-transparent hover:text-foreground'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {tab === 'KPIs' && (
          <div className="space-y-3">
            {data.kpis.map((kpi) => (
              <div key={`inspector-kpi-${kpi.label}`} className="bg-muted/40 rounded-lg p-3">
                <p className="text-2xs text-muted-foreground mb-1">{kpi.label}</p>
                <p className="text-sm font-bold text-foreground font-tabular">{kpi.value}</p>
                <div className={`flex items-center gap-1 mt-1 text-2xs ${
                  kpi.direction === 'up' ? 'text-positive' : kpi.direction === 'down' ? 'text-negative' : 'text-muted-foreground'
                }`}>
                  <Icon
                    name={kpi.direction === 'up' ? 'ArrowUpIcon' : kpi.direction === 'down' ? 'ArrowDownIcon' : 'MinusIcon'}
                    size={10}
                  />
                  {kpi.trend}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'Risks' && (
          <div className="space-y-2">
            {data.risks.length === 0 ? (
              <div className="text-center py-6">
                <Icon name="ShieldCheckIcon" size={22} className="text-positive mx-auto mb-1" />
                <p className="text-xs text-muted-foreground">No active risks</p>
              </div>
            ) : (
              data.risks.map((risk, i) => (
                <div key={`node-risk-${i}`} className="flex items-start gap-2 bg-warning/10 border border-warning/20 rounded-lg p-3">
                  <Icon name="ExclamationTriangleIcon" size={12} className="text-warning shrink-0 mt-0.5" />
                  <p className="text-xs text-foreground leading-relaxed">{risk}</p>
                </div>
              ))
            )}
          </div>
        )}

        {tab === 'Impact' && (
          <div className="space-y-3">
            <div className="bg-muted/30 rounded-lg p-3">
              <p className="text-2xs text-muted-foreground font-semibold uppercase tracking-wide mb-2">Business Impact</p>
              <p className="text-xs text-foreground leading-relaxed">{data.impact}</p>
            </div>
            <div className="bg-accent/5 border border-accent/20 rounded-lg p-3">
              <p className="text-2xs text-accent font-semibold uppercase tracking-wide mb-2">Recommendation</p>
              <p className="text-xs text-foreground leading-relaxed">{data.recommendation}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}